// LIFF API client — typed reads for the 6 LIFF pages. Always attaches the
// Bearer session token from useLiff(); throws on network / non-2xx so callers
// surface a real error UI instead of silently displaying stale or fabricated
// data.
//
// Each function lives next to its TypeScript shape so consumers can pin
// the types as they evolve.

import { apiFetch } from "../lib/api-client.js";
import type {
  SiteLiveData, IncidentData, EventData, ManagerTodayData, OfficerTasksData, ReportData,
} from "./api-types.js";

export async function fetchSiteLive(siteId: string, sessionToken: string): Promise<SiteLiveData> {
  const r = await apiFetch(`/v1/patrols/sites/${encodeURIComponent(siteId)}/live`, {}, { jwt: sessionToken });
  if (!r.ok) throw new Error(`http_${r.status}`);
  return await r.json() as SiteLiveData;
}

export async function fetchIncident(incidentId: string, sessionToken: string): Promise<IncidentData> {
  const r = await apiFetch(`/v1/patrols/incidents/${encodeURIComponent(incidentId)}`, {}, { jwt: sessionToken });
  if (!r.ok) throw new Error(`http_${r.status}`);
  return await r.json() as IncidentData;
}

export async function fetchEvent(eventId: string, sessionToken: string): Promise<EventData> {
  const r = await apiFetch(`/v1/patrols/events/${encodeURIComponent(eventId)}`, {}, { jwt: sessionToken });
  if (!r.ok) throw new Error(`http_${r.status}`);
  return await r.json() as EventData;
}

export async function fetchManagerToday(tenantId: string, sessionToken: string): Promise<ManagerTodayData> {
  const r = await apiFetch(`/v1/patrols/manager/today?tenant_id=${encodeURIComponent(tenantId)}`, {}, { jwt: sessionToken });
  if (!r.ok) throw new Error(`http_${r.status}`);
  return await r.json() as ManagerTodayData;
}

export async function fetchOfficerTasks(tenantId: string, sessionToken: string): Promise<OfficerTasksData> {
  const r = await apiFetch(`/v1/patrols/officer/tasks?tenant_id=${encodeURIComponent(tenantId)}`, {}, { jwt: sessionToken });
  if (!r.ok) throw new Error(`http_${r.status}`);
  return await r.json() as OfficerTasksData;
}

export async function fetchReport(reportId: string, sessionToken: string): Promise<ReportData> {
  const r = await apiFetch(`/v1/reports/${encodeURIComponent(reportId)}`, {}, { jwt: sessionToken });
  if (!r.ok) throw new Error(`http_${r.status}`);
  return await r.json() as ReportData;
}

export type {
  SiteLiveData, IncidentData, EventData, ManagerTodayData, OfficerTasksData, ReportData,
} from "./api-types.js";


export interface ReportListItem {
  report_id: string;
  title: string;
  scope_label: string;
  kind: "daily" | "weekly" | "monthly" | "custom";
  date_local: string;
  generated_at_local: string;
  status: "queued" | "ready" | "failed";
}
export interface ReportsListData {
  tenant_id: string;
  kind: string;
  reports: ReportListItem[];
}

export async function fetchReportsList(tenantId: string, sessionToken: string, kind: "daily"|"weekly"|"monthly" = "daily"): Promise<ReportsListData> {
  const r = await apiFetch(`/v1/reports?tenant_id=${encodeURIComponent(tenantId)}&kind=${kind}`, {}, { jwt: sessionToken });
  if (!r.ok) throw new Error(`http_${r.status}`);
  return await r.json() as ReportsListData;
}


export interface MyScheduleEntry {
  id: string;
  work_date: string;
  shift_id: string;
  site_id: string;
  status: "scheduled" | "confirmed" | "swapped";
  notes: string | null;
  shift_name: string | null;
  shift_start: string | null;
  shift_end: string | null;
  site_name: string | null;
}
export interface MyScheduleException {
  id: string;
  site_id: string | null;
  exception_date: string;
  extends_until: string | null;
  kind: "holiday" | "typhoon" | "closure" | "extra_coverage" | "custom";
  auto_skip: number;
  label: string;
}
export interface MyScheduleData {
  items: MyScheduleEntry[];
  exceptions: MyScheduleException[];
  from: string;
  to: string;
}

export async function fetchMySchedule(sessionToken: string, from?: string, to?: string): Promise<MyScheduleData> {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  const path = `/v1/schedules/me${qs.toString() ? `?${qs}` : ""}`;
  const r = await apiFetch(path, {}, { jwt: sessionToken });
  if (!r.ok) throw new Error(`http_${r.status}`);
  return await r.json() as MyScheduleData;
}

/** Officer-side: post a swap request asking someone else to take/swap own shift. */
export async function createSwapRequest(
  sessionToken: string,
  body: {
    requester_roster_entry_id: string;
    /** Pick a specific cover, or null = ask manager to assign anyone. */
    target_officer_id: string | null;
    target_roster_entry_id?: string | null;
    reason?: string | null;
  }
): Promise<{ id: string; ok: true }> {
  const r = await apiFetch(
    `/v1/schedules/swap-requests`,
    { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) },
    { jwt: sessionToken },
  );
  if (!r.ok) {
    const code = await r.json().catch(() => ({ error: "request_failed" })) as { error?: string };
    throw new Error(code.error ?? `http_${r.status}`);
  }
  return await r.json() as { id: string; ok: true };
}

/** Cancel own swap request. */
export async function cancelSwapRequest(sessionToken: string, id: string): Promise<void> {
  const r = await apiFetch(
    `/v1/schedules/swap-requests/${encodeURIComponent(id)}`,
    { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify({ decision: "cancelled" }) },
    { jwt: sessionToken },
  );
  if (!r.ok) throw new Error(`http_${r.status}`);
}


// ─── Insufficient-scope handling ─────────────────────────────────────────

export class ApiInsufficientScopeError extends Error {
  readonly code = "insufficient_scope";
  constructor(public readonly required: string[], public readonly have: string[]) {
    super(`insufficient_scope: required=${required.join(",")} have=${have.join(",")}`);
    this.name = "ApiInsufficientScopeError";
  }
}

/** Wrapper around fetch that throws ApiInsufficientScopeError on 403 with code=insufficient_scope. */
export async function fetchWithScopeCheck(p: () => Promise<Response>): Promise<Response> {
  const r = await p();
  if (r.status === 403) {
    try {
      const body = await r.clone().json() as { code?: string; required?: string[]; have?: string[] };
      if (body.code === "insufficient_scope") {
        throw new ApiInsufficientScopeError(body.required ?? [], body.have ?? []);
      }
    } catch (e) {
      if (e instanceof ApiInsufficientScopeError) throw e;
      // not JSON / no code field — fall through to caller
    }
  }
  return r;
}
